import { useCallback, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import {
  CallIntelConstant,
  CallIntelligenceV2,
  CallIntelQueries,
  CallIntelTypes,
  CallIntelUtils,
} from '@frontend/api-call-intel';
import { DemoChip } from '@frontend/analytics-shared';
import { useCallIntelDefaultsShallowStore } from '@frontend/call-intel-core';
import { Chart, CustomAxisTickParams, XAxisLabelValueTick } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { formatters } from '@frontend/shared';
import { sentry } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { callIntelligenceUtils } from '../../../utils/call-intelligence';
import { OverviewSummaryOpportunitiesStackV2 } from '../charts/overview-summary-opportunities-stack';
import { CustomTooltipTitle } from '../custom-tooltip-title';
import { useActiveFilters } from '../hooks/use-active-filters';
import { useCallIntelDemoFlags } from '../hooks/use-call-intel-demo-flags';
import { useCallIntelShallowStore, type CallIntelSubViewId } from '../hooks/use-call-intel-store';
import { SubViewOpportunitiesInlineStatsV2 } from './sub-view-opportunities-inline-stats';

type Props = {
  metricsFor?: string;
  onChangeLoadingState?: (isLoading: boolean) => void;
  sentimentCode: CallIntelSubViewId;
};

const formatChartData = <T extends Record<string, any>>(data?: T | null) => {
  return {
    groups: Object.entries(data || {}).map(([name, value]) => ({
      name,
      values: {
        [name]: value,
      },
    })),
  };
};

export const OpportunitiesBySentimentsSubView = ({ metricsFor, onChangeLoadingState, sentimentCode }: Props) => {
  const { t } = useTranslation('analytics');
  const { showDemoChipAndBanner } = useCallIntelDemoFlags();
  const { chartLabelColors, dataColors, dataLabels } = useCallIntelDefaultsShallowStore(
    'chartLabelColors',
    'dataColors',
    'dataLabels'
  );
  const { subView } = useCallIntelShallowStore('subView');
  const { filterHintText, filters } = useActiveFilters();

  const drillDownFilters = useMemo(() => callIntelligenceUtils.getDrillDownFilterKeys(subView), [subView]);

  const overviewDetailsRequestPayload = useMemo(
    () => ({
      drillDownOptions: drillDownFilters,
      filters,
      includesAnalysis: [
        CallIntelTypes.IncludesAnalysis.CALL_APPOINTMENT_TYPE,
        CallIntelTypes.IncludesAnalysis.CALL_CATEGORY,
        CallIntelTypes.IncludesAnalysis.CALL_SENTIMENT,
      ],
      includesHydration: [],
    }),
    [drillDownFilters, filters]
  );

  const { data: overviewDetails, isLoading: isLoadingOverviewDetails } = CallIntelQueries.useGetOverviewDetails({
    payload: overviewDetailsRequestPayload,
    realQueryOptions: {
      staleTime: 5 * 60 * 1000,
      onError: (error) => {
        sentry.warn({
          team: 'analytics',
          topic: 'call-intelligence',
          error: `Error fetching overview details: ${error}`,
          addContext: {
            name: 'filters',
            context: { payload: overviewDetailsRequestPayload },
          },
        });
      },
    },
  });

  const schedulingAggregateStatsRequestPayload = useMemo(
    () => CallIntelUtils.generateChartsPayload({ filters, drillDownOptions: drillDownFilters }),
    [drillDownFilters, filters]
  );

  const { data: schedulingStats, isLoading: isLoadingSchedulingStats } =
    CallIntelligenceV2.Queries.useGetSchedulingAggregateStatsQuery({
      request: schedulingAggregateStatsRequestPayload,
      options: {
        enabled: !!filters.locations?.length,
        retry: false,
        cacheTime: CallIntelConstant.DEFAULT_CACHE_TIME,
        staleTime: CallIntelConstant.DEFAULT_CACHE_TIME,
        onError: (error) => {
          sentry.warn({
            team: 'analytics',
            topic: 'call-intelligence',
            error: `Error fetching scheduling aggregate stats: ${error}`,
            addContext: {
              name: 'filters',
              context: { payload: schedulingAggregateStatsRequestPayload },
            },
          });
        },
      },
    });

  const chartData = useMemo(() => {
    return {
      appointmentTypes: formatChartData(overviewDetails?.appointmentTypes),
      category: formatChartData(overviewDetails?.categories),
    };
  }, [overviewDetails]);

  const renderCategoriesTooltipTitle = useCallback(
    ({ groupName }: { groupName: string }) => (
      <CustomTooltipTitle addPrefix label={dataLabels.categories?.[groupName]} />
    ),
    [dataLabels.categories]
  );

  const renderAppointmentTypesTooltipTitle = useCallback(
    ({ groupName }: { groupName: string }) => (
      <CustomTooltipTitle addPrefix label={dataLabels.appointmentTypes?.[groupName]} />
    ),
    [dataLabels.appointmentTypes]
  );

  const renderCategoriesXAxisTick = useCallback(
    ({ labels, groupName, ...rest }: CustomAxisTickParams) => (
      <XAxisLabelValueTick
        {...rest}
        color={chartLabelColors.categories?.[groupName]}
        label={labels?.[groupName] || groupName}
        value={formatters.value(overviewDetails?.categories?.[groupName as keyof typeof overviewDetails.categories])}
      />
    ),
    [chartLabelColors.categories, overviewDetails]
  );

  const renderAppointmentTypesXAxisTick = useCallback(
    ({ labels, groupName, ...rest }: CustomAxisTickParams) => (
      <XAxisLabelValueTick
        {...rest}
        color={chartLabelColors.appointmentTypes?.[groupName]}
        label={labels?.[groupName] || groupName}
        value={formatters.value(
          overviewDetails?.appointmentTypes?.[groupName as keyof typeof overviewDetails.appointmentTypes]
        )}
      />
    ),
    [chartLabelColors.appointmentTypes, overviewDetails]
  );

  const barChartConfig = useMemo(() => {
    const sentimentLabel = dataLabels.sentiments?.[sentimentCode];
    return [
      {
        id: 'categories',
        title: t('{{name}} Calls - Call Categories', {
          name: sentimentLabel,
        }),
        colors: dataColors.categories || {},
        dataLabels: dataLabels.categories,
        chartData: chartData?.category,
        appearance: {
          customTooltipTitle: renderCategoriesTooltipTitle,
          customXAxisTick: renderCategoriesXAxisTick,
          margin: { bottom: 20 },
          showXAxis: true,
        },
      },
      {
        id: 'appointmentTypes',
        title: t('{{name}} Calls - Appointment Types', {
          name: sentimentLabel,
        }),
        colors: dataColors.appointmentTypes || {},
        dataLabels: dataLabels.appointmentTypes,
        chartData: chartData?.appointmentTypes,
        appearance: {
          customTooltipTitle: renderAppointmentTypesTooltipTitle,
          customXAxisTick: renderAppointmentTypesXAxisTick,
          margin: { bottom: 20 },
          showXAxis: true,
        },
      },
    ];
  }, [
    dataLabels.sentiments,
    dataLabels.categories,
    dataLabels.appointmentTypes,
    sentimentCode,
    t,
    dataColors.categories,
    dataColors.appointmentTypes,
    chartData?.category,
    chartData?.appointmentTypes,
    renderCategoriesTooltipTitle,
    renderCategoriesXAxisTick,
    renderAppointmentTypesTooltipTitle,
    renderAppointmentTypesXAxisTick,
  ]);

  useEffect(() => {
    onChangeLoadingState?.(isLoadingOverviewDetails);
  }, [isLoadingOverviewDetails, onChangeLoadingState]);

  return (
    <>
      <SubViewOpportunitiesInlineStatsV2
        data={schedulingStats?.current}
        isLoading={isLoadingSchedulingStats}
        metricsFor={metricsFor}
      />

      <OverviewSummaryOpportunitiesStackV2 data={schedulingStats?.current} isLoading={isLoadingSchedulingStats} />

      <Chart.HorizontalContainer marginBottom={0} wrapperStyles={styles.container}>
        {barChartConfig.map((config) => (
          <Chart
            key={config.id}
            colors={config.colors}
            commonTooltipLabel={t('Calls')}
            labels={config.dataLabels}
            css={styles.chartStyles}
          >
            <Chart.Header
              leftElement={showDemoChipAndBanner ? <DemoChip /> : null}
              subtitle={filterHintText}
              title={config.title}
              removeHeaderBottomMargin
            />
            <Chart.BarChart appearance={config.appearance} data={config.chartData} formatValue={formatters.value} />
          </Chart>
        ))}
      </Chart.HorizontalContainer>
    </>
  );
};

const styles = {
  container: css`
    gap: ${theme.spacing(3)};
    @container (max-width: ${breakpoints.xsmall.max}px) {
      gap: ${theme.spacing(2)};
    }
  `,
  chartStyles: css`
    .chart-content {
      padding: ${theme.spacing(3)};
    }
  `,
};
